import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Pencil, Share2, Trash2, X, Youtube } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import useTranslation from "../../client/hooks/useTranslation";
import { sanitizeHtml } from "../../client/utils/sanitize";
import NotebookEditor from "../../notebook/LazyNotebookEditor";
import { SessionChart } from "../../trading-sessions/SessionChart";
import { SessionErrorBadges } from "../../trading-sessions/SessionErrorBadges";
import { SessionErrorSelect } from "../../trading-sessions/SessionErrorSelect";
import { extractYouTubeId, getMoodEmoji, getMoodLabel, getPerformanceColor, getPerformanceLabel, sessionChartWindow } from "../../trading-sessions/sessionUtils";
import { parisOffsetLabel, useSessionDetail } from "../../trading-sessions/useSessionDetail";
import { useLiteData } from "../data/LiteDataProvider";
import { TradeDrawer } from "../ui/TradeDrawer";
import { SessionAccounts } from "./sessions/SessionAccounts";
import { TradeRow } from "../ui/TradeList";
import { Card, Empty, Pnl, cardCls, labelCls } from "../ui/ui";
import { embedChartCls } from "./sessions/LastSession";
import { LiveSession } from "./sessions/LiveSession";
import { Chip, MoodChip, PerfChip, useDurationLabel } from "./sessions/SessionBits";
import { MOODS, PERFORMANCES, tradesOfSession, type RawSession } from "./sessions/sessionData";

const fieldCls = "w-full h-9 rounded-xl bg-muted border border-border px-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40";

/**
 * Détail d'une session, version Lite. Toute la logique (sauvegardes, autosave
 * des notes, vidéo, partage, suppression) vient de useSessionDetail, la même
 * que la page Advanced. Rangement : le bilan en tête (résultat, trades,
 * réussite, durée, extrêmes), l'évaluation à portée de main, puis le
 * graphique, les trades, les comptes et les notes.
 */
export default function LiteSessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const d = useSessionDetail(sessionId);
  const { t, session } = d;
  const tl = t.lite.sessions;
  const td = t.sessionDetail;
  const { fmt, trades, rawAccounts } = useLiteData();
  const [selected, setSelected] = useState<string | null>(null);
  const duration = useDurationLabel();

  const own = useMemo(() => (session ? tradesOfSession(session as unknown as RawSession, trades) : []), [session, trades]);

  if (d.isLoading) {
    return <div className="flex items-center justify-center min-h-[300px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }
  if (!session) {
    return (
      <div className="pt-6 space-y-4">
        <BackLink label={td.back} />
        <div className={cardCls}><Empty title={td.notFound} /></div>
      </div>
    );
  }

  const s = session as unknown as RawSession & { mentorNotes?: { id: string; content: string | null }[]; isActive: boolean };
  const closed = own.filter(x => x.closed);
  const wins = closed.filter(x => x.pnl + x.fees > 0).length;
  const start = s.startedAt ? new Date(s.startedAt) : null;
  const end = s.endedAt ? new Date(s.endedAt) : null;
  const selectedErrors = (s.errors ?? []).filter(e => d.errorIds.includes(e.id));

  // Lignes de P&L sans compte visible : la table des comptes ne les montre pas.
  const visibleIds = new Set(rawAccounts.map(a => a.id));
  const otherRows = d.pnlByAccount.filter(r => !r.accountId || !visibleIds.has(r.accountId));

  return (
    <div className="pt-2 md:pt-4 pb-8 space-y-5 lite-stagger">
      {/* Fil + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <BackLink label={td.back} />
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => d.setIsShareDialogOpen(true)}>
            <Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{td.shareButton}</span>
          </Button>
          {d.confirmDelete ? (
            <>
              <span className="text-xs text-muted-foreground max-w-[240px]">{td.deleteConfirm}</span>
              <Button variant="destructive" size="sm" onClick={d.handleDelete}>{td.delete}</Button>
              <Button variant="outline" size="sm" onClick={() => d.setConfirmDelete(false)} aria-label={t.common.cancel}><X className="h-4 w-4" /></Button>
            </>
          ) : (
            <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => d.setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{td.delete}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Session encore en cours : le chrono et l'arrêt, ici même. */}
      {s.isActive && (
        <section className={`${cardCls} p-5 md:p-6`}><LiveSession trades={trades} currentSessionId={s.id} hideIdle /></section>
      )}

      {/* ── 1. Bilan ── */}
      <section className={`${cardCls} p-5 md:p-6 space-y-5`}>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="relative group">
              <input
                type="text"
                value={d.sessionName}
                onChange={e => d.setSessionName(e.target.value.slice(0, 100))}
                onBlur={() => d.handleSaveName(d.sessionName)}
                placeholder={td.namePlaceholder}
                maxLength={100}
                aria-label={td.nameLabel}
                className="w-full bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none font-display text-2xl md:text-[28px] font-bold tracking-tight py-0.5 pr-12 transition-colors placeholder:text-muted-foreground/50 placeholder:font-normal placeholder:text-xl"
              />
              <Pencil size={14} className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground/50 group-focus-within:hidden pointer-events-none" />
              {d.sessionName.length > 0 && (
                <span className="absolute right-0 -bottom-4 text-[10px] text-muted-foreground/60 opacity-0 group-focus-within:opacity-100 transition-opacity">{d.sessionName.length}/100</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              {start ? fmt.dateLong(start) : ""}
              {start && <> · <span className="font-mono">{fmt.time(start.toISOString())}{end ? ` - ${fmt.time(end.toISOString())}` : ""}</span></>}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <MoodChip mood={d.mood} />
              <PerfChip performance={d.performance} />
              {s.isAutoGenerated && <Chip className="bg-primary/10 text-primary">{t.sessions.autoBadge}</Chip>}
              {d.videoUrl && <Chip><Youtube size={12} />{tl.detail.video}</Chip>}
              <SessionErrorBadges errors={selectedErrors} />
            </div>
          </div>
          {d.sessionPnl !== null && (
            <div className="sm:text-right shrink-0">
              <div className="text-xs text-muted-foreground">{td.pnl}</div>
              <Pnl value={d.sessionPnl} decimals className="text-3xl md:text-4xl font-semibold tracking-tight block mt-1" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-5 pt-5 border-t border-border">
          <Metric label={tl.detail.stats.trades} value={own.length} hint={closed.length ? t.lite.sessions.analysis.wl(wins, closed.length - wins) : undefined} />
          <Metric label={tl.detail.stats.winRate} value={closed.length ? fmt.pct(wins / closed.length) : "-"} />
          <Metric label={tl.detail.stats.duration} value={duration(s.actualDuration)} hint={s.plannedDuration ? tl.detail.stats.plannedHint(duration(s.plannedDuration)) : undefined} />
          {/* Extrêmes de P&L latent : saisis à la main, modifiables ici. */}
          <EditableMoney label={td.maxRunup} value={d.maxRunup} onChange={d.setMaxRunup} onBlur={d.handleMaxRunupBlur} tone="text-pnlPositive" />
          <EditableMoney label={td.maxDrawdown} value={d.maxDrawdown} onChange={d.setMaxDrawdown} onBlur={d.handleMaxDrawdownBlur} tone="text-pnlNegative" />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5 items-start">
        {/* Colonne latérale en premier dans le DOM : sur mobile, l'évaluation passe avant le graphique. */}
        <div className="space-y-5 min-w-0 lg:order-2">
          <Card title={tl.detail.evaluation} bodyClassName="space-y-4">
            <div>
              <div className={labelCls}>{t.session.mood}</div>
              <div className="grid grid-cols-5 gap-1.5">
                {MOODS.map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => d.handleMoodChange(m)}
                    aria-pressed={d.mood === m}
                    title={getMoodLabel(m, t)}
                    className={`h-14 rounded-xl border flex flex-col items-center justify-center gap-0.5 transition-colors ${d.mood === m ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                  >
                    <span className="text-lg leading-none">{getMoodEmoji(m)}</span>
                    <span className={`text-[10px] truncate max-w-full px-1 ${d.mood === m ? "text-foreground" : "text-muted-foreground"}`}>{getMoodLabel(m, t)}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className={labelCls}>{t.session.performance}</div>
              <div className="grid grid-cols-2 gap-1.5">
                {PERFORMANCES.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => d.handlePerformanceChange(p)}
                    aria-pressed={d.performance === p}
                    className={`h-9 rounded-xl border text-sm font-medium transition-colors ${d.performance === p ? `border-primary bg-primary/10 ${getPerformanceColor(p)}` : "border-border text-muted-foreground hover:bg-muted"}`}
                  >
                    {getPerformanceLabel(p, t)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className={labelCls}>{t.session.errors}</div>
              <SessionErrorSelect value={d.errorIds} onChange={d.handleErrorsChange} />
            </div>
            <div>
              <label className={labelCls} htmlFor="lite-mental">{td.mentalState}</label>
              <input id="lite-mental" type="text" value={d.mentalState} onChange={e => d.setMentalState(e.target.value)} onBlur={d.handleMentalStateBlur} placeholder={t.session.mentalStatePlaceholder} className={fieldCls} />
            </div>
          </Card>

          <Card title={tl.detail.timing} bodyClassName="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
              <div>
                <label className={labelCls} htmlFor="lite-start">{td.startedAt}</label>
                <input id="lite-start" type="datetime-local" value={d.startedAt} onChange={e => d.setStartedAt(e.target.value)} onBlur={d.handleStartedAtBlur} className={fieldCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="lite-end">{td.endedAt}</label>
                <input id="lite-end" type="datetime-local" value={d.endedAt} onChange={e => d.setEndedAt(e.target.value)} onBlur={d.handleEndedAtBlur} className={fieldCls} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground/80 flex items-center gap-1"><Clock className="h-3 w-3" />{parisOffsetLabel()}</p>
            <div className="grid grid-cols-2 gap-4 pt-1">
              <Metric small label={td.duration} value={duration(s.actualDuration)} />
              <Metric small label={td.planned} value={duration(s.plannedDuration)} />
            </div>
          </Card>
          {/* Enregistrement vidéo : en colonne latérale, visible sans descendre sous les notes. */}
          <VideoCard d={d} />
        </div>

        <div className="space-y-5 min-w-0 lg:order-1">
          {/* Graphique des trades de la session (absent quand elle n'a aucun trade). */}
          {d.sessionTrades.length > 0 && (
            <div className={`${cardCls} overflow-hidden ${embedChartCls}`}>
              <SessionChart session={sessionChartWindow(s, d.sessionTrades)} trades={d.sessionTrades} accounts={rawAccounts} isDark={d.isDark} />
            </div>
          )}

          <Card title={td.trades} action={<span className="text-xs text-muted-foreground">{t.lite.common.trades(own.length)}</span>} pad={false}>
            {own.length ? (
              <div className="divide-y divide-border border-t border-border mt-3">
                {own.map(tr => <TradeRow key={tr.id} trade={tr} onSelect={x => setSelected(x.id)} selected={tr.id === selected} />)}
              </div>
            ) : (
              <p className="px-5 pb-5 pt-3 text-sm text-muted-foreground">{td.noTrades}</p>
            )}
          </Card>

          {/* Comptes tradés pendant la session (contribution de la session à l'objectif). */}
          <SessionAccounts pnlByAccount={d.pnlByAccount} />
          {otherRows.length > 0 && (
            <div className={`${cardCls} px-5 py-3 space-y-1.5`}>
              <div className="text-xs text-muted-foreground">{tl.detail.otherAccounts}</div>
              {otherRows.map(r => (
                <div key={r.accountId ?? "none"} className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate">{r.name ?? td.noAccount}</span>
                  <Pnl value={r.pnl} decimals className="text-sm font-medium" />
                </div>
              ))}
            </div>
          )}

          <Card title={td.notes} pad={false}>
            <div
              className="border-t border-border mt-3 [&_.border.rounded-lg]:border-0 [&_.border.rounded-lg]:rounded-none"
              onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) d.handleSaveNotes(); }}
            >
              <NotebookEditor
                content={d.htmlNotes}
                onChange={html => d.setHtmlNotes(html)}
                placeholder={t.session.notesPlaceholder}
                onImageUpload={d.handleImageUpload}
                resolveImageKeys={d.handleResolveImageKeys}
              />
            </div>
          </Card>

          {/* Notes du tuteur : seulement s'il y en a. */}
          {(s.mentorNotes ?? []).map(note => (
            <section key={note.id} className={`${cardCls} border-primary/30 overflow-hidden`}>
              <header className="px-5 py-3 border-b border-primary/20 bg-primary/5 text-sm font-semibold text-primary">{t.mentor.mentorNotes}</header>
              <div className="p-5 prose prose-sm dark:prose-invert max-w-none text-sm text-foreground" dangerouslySetInnerHTML={{ __html: sanitizeHtml(note.content ?? "") }} />
            </section>
          ))}

        </div>
      </div>

      {d.dialogs}
      <TradeDrawer tradeId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function VideoCard({ d }: { d: ReturnType<typeof useSessionDetail> }) {
  const td = d.t.sessionDetail;
  const videoId = d.videoUrl && !d.isEditingVideo ? extractYouTubeId(d.videoUrl) : null;
  return (
    <Card
      title={td.videoTitle}
      pad={false}
      action={d.videoUrl && !d.isEditingVideo ? (
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => { d.setVideoInput(d.videoUrl); d.setIsEditingVideo(true); d.setVideoInputError(""); }} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground" title={td.videoEdit} aria-label={td.videoEdit}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={d.handleDeleteVideo} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-destructive" title={td.videoDelete} aria-label={td.videoDelete}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : undefined}
    >
      {videoId ? (
        <div className="relative w-full mt-3" style={{ paddingTop: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${videoId}`}
            title={td.videoTitle}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (!d.videoUrl || d.isEditingVideo) && (
        <div className="px-5 pb-5 pt-3 space-y-3">
          {!d.isEditingVideo && <p className="text-sm text-muted-foreground">{td.videoInvite}</p>}
          <div className="flex flex-col sm:flex-row gap-2">
            <input type="url" value={d.videoInput} onChange={e => { d.setVideoInput(e.target.value); d.setVideoInputError(""); }} placeholder={td.videoPlaceholder} className={fieldCls} aria-label={td.videoTitle} />
            <div className="flex gap-2 shrink-0">
              <Button size="sm" className="h-9" onClick={d.handleSaveVideo}>{td.videoSave}</Button>
              {d.isEditingVideo && <Button size="sm" variant="outline" className="h-9" onClick={() => { d.setIsEditingVideo(false); d.setVideoInputError(""); }}>{td.videoCancel}</Button>}
            </div>
          </div>
          {d.videoInputError && <p className="text-xs text-destructive">{d.videoInputError}</p>}
        </div>
      )}
    </Card>
  );
}

function Metric({ label, value, hint, small }: { label: string; value: ReactNode; hint?: ReactNode; small?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-mono tabular-nums font-semibold mt-1 truncate ${small ? "text-sm" : "text-xl"}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground/80 mt-0.5 truncate">{hint}</div>}
    </div>
  );
}

/** Montant saisi à la main, modifiable directement dans le bilan (enregistré à la sortie du champ). */
function EditableMoney({ label, value, onChange, onBlur, tone }: { label: string; value: string; onChange: (v: string) => void; onBlur: () => void; tone: string }) {
  return (
    <label className="min-w-0 block">
      <span className="flex items-center gap-1 text-xs text-muted-foreground">{label}<Pencil size={10} className="opacity-60" /></span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="-"
        className={`mt-1 w-full bg-transparent border-0 border-b border-dashed border-border hover:border-muted-foreground focus:border-primary focus:outline-none font-mono tabular-nums text-xl font-semibold py-0 placeholder:text-muted-foreground ${value ? tone : ""}`}
      />
    </label>
  );
}

function BackLink({ label }: { label: string }) {
  return (
    <Link to="/trading-sessions" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
      <ArrowLeft className="h-4 w-4" />{label}
    </Link>
  );
}
